// ============================================================================
// CANCUN100 — recursos.js  (Bloque 31: carga bajo demanda con caché en memoria)
// ----------------------------------------------------------------------------
// useRecursos(cargadores): cada recurso (p. ej. 'ventas', 'cuponesAdmin') tiene un cargador async que pide
// a Supabase SOLO lo suyo y guarda en el estado de App. `cargar(nombres, { clave })` pide los que no estén
// frescos (< 2 min con la misma clave) y nunca repite una petición en vuelo; `invalidar(nombres)` marca
// recursos para que la próxima vez se vuelvan a pedir (sin pedirlos ya). La clave distingue variantes
// (90 días / histórico, agente / admin). Nada de setInterval aquí.
// ============================================================================
import { useRef, useState, useCallback } from 'react';

export const TTL_RECURSOS = 2 * 60 * 1000; // 2 minutos

export function useRecursos(cargadoresRef) {
  const cache = useRef(new Map()); // nombre → { cargadoEn, clave, promesa }
  const [enVuelo, setEnVuelo] = useState(0);

  const estaFresco = useCallback((nombre, clave = '') => {
    const e = cache.current.get(nombre);
    return !!e && e.clave === clave && !e.promesa && Date.now() - e.cargadoEn < TTL_RECURSOS;
  }, []);

  // Espera a que un recurso termine de cargar (lo usan cargadores que dependen de otro, p. ej. catálogo ← productos).
  const esperar = useCallback(async (nombre) => { const e = cache.current.get(nombre); if (e && e.promesa) { try { await e.promesa; } catch { /* ya se reportó */ } } }, []);

  const cargar = useCallback(async (nombres, { forzar = false, clave = '' } = {}) => {
    const lista = [...new Set([].concat(nombres).filter(Boolean))];
    const tareas = [];
    for (const nombre of lista) {
      const fn = cargadoresRef.current[nombre];
      if (!fn) continue;
      const e = cache.current.get(nombre);
      if (e && e.promesa && e.clave === clave) { tareas.push(e.promesa); continue; } // ya va en vuelo
      if (!forzar && estaFresco(nombre, clave)) continue;
      setEnVuelo((n) => n + 1);
      const promesa = (async () => {
        try { await fn({ esperar }); cache.current.set(nombre, { cargadoEn: Date.now(), clave, promesa: null }); }
        catch (err) { console.error(`[recursos] ${nombre}:`, err); cache.current.delete(nombre); }
        finally { setEnVuelo((n) => Math.max(0, n - 1)); }
      })();
      cache.current.set(nombre, { cargadoEn: 0, clave, promesa });
      tareas.push(promesa);
    }
    await Promise.all(tareas);
  }, [cargadoresRef, estaFresco, esperar]);

  const invalidar = useCallback((nombres) => {
    if (nombres == null) { cache.current.clear(); return; }
    [].concat(nombres).forEach((n) => cache.current.delete(n));
  }, []);

  return { cargar, invalidar, estaFresco, cargando: enVuelo > 0 };
}
